import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === "true";
const STATIC_EMAIL = "juan@sahagunlandscape.com";

const PERKS = [
  {
    title: "Steady Year-Round Work",
    desc: "Landscaping, maintenance, and irrigation contracts keep our crews busy in every season.",
  },
  {
    title: "Learn From The Owner",
    desc: "Work side-by-side with Juan Sahagun and build real skills — design, hardscape, irrigation, tree care.",
  },
  {
    title: "Family Crew Culture",
    desc: "A tight family-owned team where good work is noticed and everyone pulls together.",
  },
];

const POSITIONS = [
  "Landscape Crew Member",
  "Maintenance Gardener",
  "Irrigation Technician",
  "Tree Care Assistant",
  "General Application",
];

const INITIAL = { name: "", phone: "", position: "Landscape Crew Member", experience: "" };
const inputCls =
  "px-4 py-3 bg-[#faf8f5] border border-[#2d4a22]/30 text-sm focus:border-[#2d4a22] focus:outline-none w-full";
const labelCls = "text-xs uppercase font-bold tracking-wider text-[#1f281e]";

const CareersPage = () => {
  const ref = useReveal();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Careers — Work With Us | Sahagun Landscape";
    window.scrollTo(0, 0);
    return () => {
      document.title = "Sahagun Landscape | Landscaping & Hardscaping — Paramount, CA";
    };
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (STATIC_MODE) {
        const res = await fetch(`https://formsubmit.co/ajax/${STATIC_EMAIL}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            _subject: `New Job Application - ${form.position}`,
            _template: "table",
            name: form.name,
            phone: form.phone,
            position: form.position,
            experience: form.experience || "-",
          }),
        });
        if (!res.ok) throw new Error("send failed");
      } else {
        await axios.post(`${API}/jobs`, form);
      }
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Application sent. We'll be in touch.");
    } catch (err) {
      toast.error("Could not send your application. Please call +1 (323) 220-4640.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={ref} data-testid="careers-page" className="bg-[#faf8f5] text-[#1f281e]">
      {/* Page hero */}
      <section className="relative py-20 md:py-28 border-b border-[#2d4a22]/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] d1-drift pointer-events-none"
          style={{ backgroundImage: "radial-gradient(#2d4a22 1.5px, transparent 1.5px)" }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-4">
            Careers
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.95] max-w-3xl">
            <span className="ln"><span>Work With Us.</span></span>
            <span className="ln"><span className="text-[#2d4a22]">Grow With Us.</span></span>
          </h1>
          <p className="text-lg text-[#3d4b3b] max-w-xl leading-relaxed mt-6">
            Join a family-owned crew building and maintaining landscapes across Greater Los Angeles.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              Why Sahagun
            </span>
            <h2 className="font-display text-3xl md:text-4xl uppercase">Life On The Crew</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PERKS.map((p, i) => (
              <div key={p.title} className={`reveal reveal-d${i + 1} p-8 bg-[#f2ece1] border border-[#2d4a22]/10`} data-testid={`perk-${i + 1}`}>
                <span className="block w-8 h-1 bg-[#2d4a22] organic-grow mb-5" />
                <h3 className="text-lg font-bold uppercase mb-3">{p.title}</h3>
                <p className="text-sm text-[#3d4b3b] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles + form */}
      <section className="py-20 md:py-24 bg-[#f2ece1] border-t border-[#2d4a22]/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
              Open Roles
            </span>
            <h2 className="font-display text-3xl md:text-4xl uppercase mb-6">Positions We Hire</h2>
            <ul className="divide-y divide-[#2d4a22]/10" data-testid="positions-list">
              {POSITIONS.map((pos) => (
                <li key={pos} className="py-4 flex items-center justify-between">
                  <span className="text-sm font-semibold uppercase tracking-wide">{pos}</span>
                  <span className="font-mono-type text-[11px] text-[#2d4a22] uppercase">Greater LA</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#3d4b3b] mt-6 leading-relaxed">
              Don't see an exact fit? Send a general application — good people always get a call back.
            </p>
          </div>

          <div className="lg:col-span-7 reveal reveal-d1">
            <div className="bg-[#faf8f5] p-8 md:p-10 border border-[#2d4a22]/20 shadow-sm">
              <h3 className="font-display text-2xl uppercase mb-1">Apply Now</h3>
              <p className="text-xs text-[#3d4b3b] mb-8">Two minutes — Juan reviews every application personally.</p>

              {submitted ? (
                <div className="text-center py-8" data-testid="job-success">
                  <svg className="w-12 h-12 mx-auto mb-4 text-[#2d4a22]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <h4 className="font-display text-xl uppercase mb-2">Application Received</h4>
                  <p className="text-sm text-[#3d4b3b] mb-6">Thank you — we'll call you to talk next steps.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="job-another-btn"
                    className="px-8 py-3 border border-[#2d4a22] text-[#2d4a22] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#2d4a22]/5 transition-colors"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form className="grid grid-cols-1 sm:grid-cols-2 gap-6" onSubmit={handleSubmit} data-testid="job-form">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="job-name" className={labelCls}>Your Name</label>
                    <input id="job-name" data-testid="job-name-input" type="text" required value={form.name} onChange={update("name")} className={inputCls} placeholder="First and last name" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="job-phone" className={labelCls}>Phone Number</label>
                    <input id="job-phone" data-testid="job-phone-input" type="tel" required value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(323) 000-0000" />
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-1">
                    <label htmlFor="job-position" className={labelCls}>Position</label>
                    <select id="job-position" data-testid="job-position-select" value={form.position} onChange={update("position")} className={inputCls}>
                      {POSITIONS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2 flex flex-col gap-1">
                    <label htmlFor="job-experience" className={labelCls}>Experience</label>
                    <textarea id="job-experience" data-testid="job-experience-textarea" rows="3" value={form.experience} onChange={update("experience")} className={inputCls} placeholder="Tell us about your experience (none required — willingness to learn counts)" />
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={submitting} data-testid="job-submit-btn" className="w-full py-4 bg-[#2d4a22] text-[#faf8f5] text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#1a2d14] transition-colors disabled:opacity-60">
                      {submitting ? "Sending..." : "Submit Application"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
